/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";

import BreadCrumb from "../../components/breadCrumb";
import Loader from "../../components/PageLoader";
import ReactHookFormDropdown from "../../components/forms/react-hook-dropdown";
import FormInput from "../../components/forms/form-Input";

import { ESCALATION_LEVELS } from "./constants";
import { Paths } from "../../routes/paths";

import {
  useLookupAssignmentGroup,
  useLookupRegions,
} from "../../services/lookups-mutations";
import {
  useGetEscalationMatrixDetail,
  usePutEscalationMatrix,
} from "../../services/escalation-matrix";

import { Button, Flex } from "@chakra-ui/react";
import EmailChipInput from "components/EmailChipInput";
import ReactHookFormMultiSelect from "components/forms/react-hook-multiselect";

const ACTIVITY_TYPES = [
  { id: "Customer Service", text: "Customer Service" },
  { id: "ETISALAT Third Party", text: "ETISALAT Third Party" },
  { id: "Facilities Request", text: "Facilities Request" },
  { id: "TGM Remote Support", text: "TGM Remote Support" },
  { id: "Unusual Event", text: "Unusual Event" },
  { id: "Customer Complaints", text: "Customer Complaints" },
  { id: "Voice Of Staff", text: "Voice Of Staff" },
  { id: "Incident - Standard", text: "Incident - Standard" },
  { id: "Incident - Emergency", text: "Incident - Emergency" },
  { id: "Panic Alert Incident", text: "Panic Alert Incident" },
  { id: "Marketing", text: "Marketing" },
  { id: "Missed And Delayed Services", text: "Missed And Delayed Services" },
  { id: "TGSI Helpdesk", text: "TGSI Helpdesk" },
];

/* ---------------- VALIDATION ---------------- */
const FORM_VALIDATION = {
  name: { required: "Please enter Name" },
  designation: { required: "Please enter Designation" },
  escalationLevel: {
    required: "Please select Escalation Level",
  },
  assignmentGroup: { required: "Please select Assignment Group" },
  region: { validate: (v) => v?.length > 0 || "Please select Region(s)" },
  activities: { validate: (v) => v?.length > 0 || "Please select Activity" },
  emails: {
    validate: (v) => v?.length > 0 || "Please enter at least one email address",
  },
};

/* ---------------- HELPERS ---------------- */
const prependAllOption = (list, key = "id", textKey = "text") => {
  if (!list) return [];
  return [{ [key]: "All", [textKey]: "All" }, ...list];
};

const handleAllSelection = (values, allKey = "id") => {
  const hasAll = values.some((v) => v[allKey] === "All");
  if (!hasAll) return values;
  return values.length > 1 ? values.filter((v) => v[allKey] !== "All") : values;
};

export default function EditEscalationMatrix() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  /* ---------------- LOOKUPS ---------------- */
  const { data: assignmentGroupsData, mutate: getAssignmentGroups } =
    useLookupAssignmentGroup();
  const { data: regionsData, mutate: getRegions } = useLookupRegions();

  const { mutate: getDetail, data: detailRes } = useGetEscalationMatrixDetail();
  const { mutate: updateMatrix } = usePutEscalationMatrix();

  /* ---------------- FORM ---------------- */
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: "",
      designation: "",
      assignmentGroup: null,
      region: [],
      activities: [],
      emails: [],
      escalationLevel: null,
      isActive: true,
    },
  });
  const normalizedRegions = useMemo(
    () =>
      [{ id: "All", name: "All" }, ...(regionsData?.data || [])].map((r) => ({
        id: String(r.id ?? r.name ?? ""),
        name: String(r.name ?? ""),
      })),
    [regionsData],
  );

  const normalizedActivities = useMemo(
    () =>
      [{ id: "All", text: "All" }, ...(ACTIVITY_TYPES || [])].map((a) => ({
        id: String(a.id ?? a.text ?? ""),
        text: String(a.text ?? ""),
      })),
    [],
  );
  const handleCancel = () => {
    reset();
    navigate(Paths.escalationMatrix);
  };

  const normalizedAssignmentGroups = useMemo(
    () =>
      prependAllOption(
        (assignmentGroupsData?.data || []).map((a) => ({
          id: a.name,
          text: a.name,
        })),
        "id",
        "text",
      ),
    [assignmentGroupsData],
  );

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    // Execute all lookups
    getAssignmentGroups();
    getRegions("UAE"); // Adjust argument as needed
    if (id) getDetail(id);
  }, [id]);
  // Fetch existing escalation matrix detail
  useEffect(() => {
    const res = detailRes?.data;
    if (!res) return;
    reset({
      name: res.name,
      designation: res.designation,
      assignmentGroup: res.assignmentGroup ? res.assignmentGroup : null,
      region:
        normalizedRegions.filter((r) => res.region?.includes(r.name)) || [],
      activities:
        normalizedActivities.filter((a) => res.activityTypes?.includes(a.id)) ||
        [],
      emails: res.emails || [],
      escalationLevel: res.escalationLevel?.[0]
        ? String(res.escalationLevel[0])
        : null,
      isActive: res.isActive,
    });

    setLoading(false);
  }, [id, reset, detailRes, normalizedRegions, normalizedActivities]);

  /* ---------------- SUBMIT ---------------- */
  const onSubmit = (data) => {
    const payload = {
      name: data.name,
      designation: data.designation,
      assignmentGroup: data.assignmentGroup?.id || data.assignmentGroup,
      region: data.region.map((r) => r.id),
      activityTypes: data.activities.map((a) => a.id),
      emails: data.emails,
      escalationLevel: [data.escalationLevel],
      isActive: data.isActive,
    };

    updateMatrix(
      { id, payload },
      {
        onSuccess: () => navigate(Paths.escalationMatrix),
      },
    );
  };

  if (loading) return <Loader />;

  return (
    <div style={{ maxWidth: "100%", margin: "0 auto", padding: "20px" }}>
      <BreadCrumb
        links={[
          { text: "Escalation Matrix", link: Paths.escalationMatrix },
          { text: "Edit Escalation Matrix", isCurrentPage: true },
        ]}
      />

      <div className="bg-white p-6 rounded shadow">
        <h2 className="text-lg font-semibold mb-4">Edit Escalation Matrix</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Active Checkbox */}
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <label className="flex items-center space-x-2 mb-2">
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
                <span>Active</span>
              </label>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <FormInput
              control={control}
              name="name"
              label="Group Name"
              errors={errors}
              rules={FORM_VALIDATION.name}
              isRequired
            />

            <FormInput
              control={control}
              name="designation"
              label="Description"
              errors={errors}
              rules={FORM_VALIDATION.designation}
              isRequired
            />
            {/* Escalation Level */}
            <ReactHookFormDropdown
              control={control}
              name="escalationLevel"
              label="Escalation Level"
              errors={errors}
              rules={FORM_VALIDATION.escalationLevel}
              isRequired
              filterable
              data={ESCALATION_LEVELS.map((e) => ({
                value: String(e.id),
                label: String(e.text),
              }))}
              placeholder="Select Escalation Level"
            />

            <ReactHookFormDropdown
              control={control}
              name="assignmentGroup"
              label="Assignment Group"
              errors={errors}
              rules={FORM_VALIDATION.assignmentGroup}
              isRequired
              filterable
              data={normalizedAssignmentGroups.map((item) => ({
                value: String(item.id),
                label: String(item.text),
              }))}
              id="id"
              text="text"
              placeholder="Select a Value"
            />

            <Controller
              control={control}
              name="emails"
              rules={FORM_VALIDATION.emails}
              render={({ field }) => (
                <EmailChipInput
                  label="Email"
                  required
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.emails?.message}
                />
              )}
            />

            {/* Activities */}
            <ReactHookFormMultiSelect
              label="Activity Types"
              name="activities"
              control={control}
              errors={errors}
              isRequired
              filterable
              data={normalizedActivities}
              id="id"
              text="text"
              rules={FORM_VALIDATION.activities}
              placeholder="Select a Value"
              onChange={(value) => handleAllSelection(value, "id")}
            />

            {/* Regions */}
            <ReactHookFormMultiSelect
              label="Regions"
              name="region"
              control={control}
              errors={errors}
              isRequired
              filterable
              data={normalizedRegions}
              id="id"
              text="name"
              rules={FORM_VALIDATION.region}
              placeholder="Select a Value"
              onChange={(value) => handleAllSelection(value, "id")}
            />
          </div>

          <Flex justify="flex-end" mt="30px" gap="10px">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>

            <Button type="submit" isLoading={isSubmitting}>
              Update Escalation Matrix
            </Button>
          </Flex>
        </form>
      </div>
    </div>
  );
}
